import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash } from "@phosphor-icons/react";
import { formatET } from "../../lib/dateUtils";
import { api, formatApiErrorDetail } from "../../lib/api";

const STATUS_STYLE = {
  pending: "bg-igc-gold/10 text-igc-goldText border-igc-gold/20",
  completed: "bg-igc-success/10 text-igc-success border-igc-success/30",
  missed: "bg-igc-danger/10 text-igc-danger border-igc-danger/40",
};

const STATUS_EXPLAIN = {
  pending: "This callback hasn't happened yet — it's scheduled for the future.",
  completed: "This rep followed through and completed this callback on time.",
  missed: "This callback's scheduled time has passed and it was never marked completed — you may want to check in with this rep.",
};

export function ManagerCallbacksTab() {
  const [callbacks, setCallbacks] = useState([]);

  const fetchCallbacks = () => {
    api.get("/manager/callbacks")
      .then(({ data }) => setCallbacks(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  useEffect(() => {
    fetchCallbacks();
  }, []);

  const deleteCallback = async (id) => {
    if (!window.confirm("Permanently delete this callback? This cannot be undone.")) return;
    try {
      await api.delete(`/manager/callbacks/${id}`);
      toast.success("Callback deleted");
      fetchCallbacks();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="manager-callbacks-tab" className="space-y-3">
      <h3 className="font-serif text-lg text-igc-cream tracking-tight mb-1" title="Every callback any rep on your team scheduled using the 'Callback Scheduled' disposition, all in one place.">Callback Calendar — Your Team</h3>
      <p className="text-igc-muted text-sm mb-4">Every scheduled callback across your reps — missed follow-ups are flagged in red.</p>
      {callbacks.length === 0 && <p className="text-igc-muted text-sm bg-igc-surface border border-igc-gold/10 p-5" data-testid="manager-callbacks-empty">No callbacks scheduled yet.</p>}
      <div className="space-y-2">
        {callbacks.map((c) => (
          <div key={c.id} className={`p-4 border flex items-center justify-between gap-4 flex-wrap ${c.status === "missed" ? "bg-igc-danger/5 border-igc-danger/40" : "bg-igc-surface border-igc-gold/15"}`} data-testid={`manager-callback-row-${c.id}`} title="A callback one of your reps promised a lead, with who's responsible and when it's due.">
            <div>
              <p className="text-igc-cream text-sm">{c.rep_name} ({c.rep_code}) — {c.lead_name}</p>
              <p className="text-igc-muted text-xs">{formatET(c.scheduled_at, "MMM d, yyyy h:mm a")} ET</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-[10px] px-2 py-1 uppercase tracking-wide border ${STATUS_STYLE[c.status]}`} title={STATUS_EXPLAIN[c.status] || "Current status of this scheduled callback."}>{c.status}</span>
              <button
                data-testid={`manager-callback-delete-${c.id}`}
                onClick={() => deleteCallback(c.id)}
                title="Permanently deletes this callback entry — unlike its status, this removes it from the database entirely and cannot be undone."
                className="text-igc-muted hover:text-igc-danger transition-colors"
              >
                <Trash size={14} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
